import { LoadingSpinner } from '../shared/components';
import { emptyState } from '../shared/components/Narrative';
import { donut } from '../shared/components/charts';
import { icon } from '../shared/components/icons';
// R120 (D14; contract C4): the board's OPEN counts — one rule with the Issues page.
import { openIssueCounts, openByProperty } from './IssuesPage';

// Portfolio Health (Loom #3) — the operational cockpit for the CEO. Revenue
// pace + Booked/Projected/Actual now live in Money; this page owns the
// operational backlog: every issue across the suite by severity, source, and
// property. Reuses /api/assetops/issues/board; no new backend.
const SEV = [
  ['emergency', 'Emergency', '#dc2626'],
  ['high', 'High', '#ef4444'],
  ['med', 'Medium', '#f59e0b'],
  ['low', 'Low', '#94a3b8'],
] as const;
const SOURCE_LABEL: Record<string, string> = {
  maintenance: 'Maintenance', inspection: 'Inspections', housekeeping: 'Housekeeping',
  incident: 'Incidents', message: 'Guest messages', review: 'Reviews',
};

export class AnalyticsPage {
  private container: HTMLElement;
  private issues: any = null;

  constructor(container: HTMLElement) { this.container = container; }

  private esc(s: any): string { return String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!)); }

  async render(): Promise<void> {
    this.container.innerHTML = `<div class="page-content"><div style="padding:48px; text-align:center;">${LoadingSpinner.render()}</div></div>`;
    this.issues = await fetch('/api/assetops/issues/board?days=60').then(r => r.ok ? r.json() : null).catch(() => null);
    this.draw();
  }

  private kpiCard(label: string, ico: string, value: string, cap: string, tone = ''): string {
    return `
      <div class="kpi-card ${tone}">
        <div class="kpi-card-top"><span class="kpi-card-label">${this.esc(label)}</span><span class="kpi-card-ic">${icon(ico, 17)}</span></div>
        <div class="kpi-card-value">${value}</div>
        <div class="kpi-card-cap">${cap}</div>
      </div>`;
  }

  private draw(): void {
    const iss = this.issues;
    if (!iss) {
      this.container.innerHTML = `<div class="page-content">${emptyState('Portfolio health unavailable', 'GET /api/assetops/issues/board failed — check the server log.', '⚠️')}</div>`;
      return;
    }
    // R120 (D14): Urgent / Emergencies / Properties affected / the donut count
    // OPEN issues only (open + in progress) — summary.by_severity counts every
    // status, so a dismissed or resolved finding used to keep "Urgent" up.
    const sev = openIssueCounts(iss);
    const bySource = iss.summary?.by_source || {};
    const urgent = sev.emergency + sev.high;
    const openN = iss.summary?.open ?? 0;
    const inProgN = Number(iss.summary?.in_progress) || 0;   // R116: issues handed to an open work order read "in progress"
    const totalIss = iss.summary?.total || 0;
    const pOpen = openByProperty(iss);
    const props: any[] = (iss.by_property || []).map((p: any) => ({ ...p, _o: pOpen.get(String(p.listing_id)) || { total: 0, high: 0, emergency: 0 } }))
      .filter((p: any) => p._o.total > 0)
      .sort((a: any, b: any) => (b._o.emergency - a._o.emergency) || (b._o.high - a._o.high) || (b._o.total - a._o.total));
    const affected = props.length;

    // the slices are open + in progress, so the centre says so when a WO holds some (the "Open issues" card counts open alone)
    const sevDonut = donut(SEV.map(([k, label, c]) => ({ label, value: sev[k] || 0, color: c })), { centerValue: String(sev.total), centerLabel: inProgN ? 'not resolved' : 'open' });

    const topProps = props.slice(0, 10).map((p: any) => `
      <div class="pq-row" style="cursor:default;">
        <div class="pq-main"><div class="pq-name">${this.esc(p.listing_name)}</div></div>
        <div class="pq-right">
          ${(p._o.emergency + p._o.high) ? `<span class="pill pill-red">${p._o.emergency + p._o.high} urgent</span>` : `<span class="pill pill-gray">${p._o.total} open</span>`}
          <span class="pq-meta"><b>${p._o.total}</b> open · ${Number(p.total) || 0} in 60 days</span>
        </div>
      </div>`).join('');

    this.container.innerHTML = `
      <div class="page-content">
        <div class="dash-head">
          <h1>Portfolio Health</h1>
          <p>The operational backlog across the portfolio — every issue by severity, source, and property. Last 60 days, as of ${this.esc(iss.as_of || '—')}. (Revenue pace lives in Money.)</p>
        </div>

        <div class="kpi-grid">
          ${this.kpiCard('Open issues', 'flag', String(openN), `${inProgN ? `+${inProgN} in progress on work orders · ` : ''}${totalIss} total in 60 days`, openN ? '' : 'tone-good')}
          ${this.kpiCard('Urgent', 'target', String(urgent), urgent ? 'open emergency + high severity' : 'none urgent', urgent ? 'tone-bad' : 'tone-good')}
          ${this.kpiCard('Properties affected', 'building', String(affected), 'with at least one open issue')}
          ${this.kpiCard('Emergencies', 'flag', String(sev.emergency), sev.emergency ? 'need immediate attention' : 'none active', sev.emergency ? 'tone-bad' : 'tone-good')}
        </div>

        <div class="kpi-grid cols-3" style="grid-template-columns: 1fr 1.6fr; margin-top:6px;">
          <div class="pq-card">
            <div class="pq-card-h"><div><h3>Issue backlog</h3><p>open now · by severity (sources: last 60 days)</p></div></div>
            <div style="padding:16px 18px;">
              ${sevDonut}
              <div style="margin-top:14px; display:flex; gap:6px; flex-wrap:wrap;">
                ${Object.entries(bySource).sort((a: any, b: any) => b[1] - a[1]).map(([s, n]) => `<span class="pill pill-gray">${this.esc(SOURCE_LABEL[s] || s)}: ${n}</span>`).join('') || '<span style="font-size:12px; color:var(--text-faint);">No issues in window.</span>'}
              </div>
            </div>
          </div>
          <div class="pq-card">
            <div class="pq-card-h"><div><h3>Properties needing attention</h3><p>ranked by open urgent, then open issues</p></div></div>
            ${topProps || '<div style="text-align:center; color:var(--text-muted); padding:24px; font-size:13px;">No open issues — clean board.</div>'}
          </div>
        </div>
      </div>`;
  }
}
